import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, tap, map, catchError, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';
import { 
  NotificationItem, 
  NotificationsData, 
  ApiResponse, 
  ActionResponse 
} from '../models/notification.model';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly toastr = inject(ToastrService);
  private readonly apiUrl = environment.apiUrl;

  // Real-time notification stream
  private notificationSubject = new Subject<NotificationItem>();
  notification$ = this.notificationSubject.asObservable();

  // Reactive unread count
  unreadCount = signal<number>(0);

  constructor() {
    this.listenForServiceWorkerMessages();
  }

  /**
   * Listen for messages from the service worker
   */
  private listenForServiceWorkerMessages(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'PUSH_NOTIFICATION') {
          // console.log('Received push notification from SW:', event.data.payload);
          this.handleIncomingNotification(event.data.payload);
        }
      });
    }
  }

  /**
   * Play notification sound
   */
  private playSound(): void {
    const audio = new Audio('assets/media/audio/smile-ringtone.ogg');
    audio.play().catch((err: any) => console.log('Audio playback failed:', err));
  }

  /**
   * Handle incoming real-time notification
   */
  handleIncomingNotification(payload: any): void {
    const notification: NotificationItem = {
      id: payload.data?.id || Date.now(),
      title: payload.notification?.title || 'New Notification',
      message: payload.notification?.body || '',
      createdAt: new Date().toISOString(),
      read: false,
      data: payload.data
    };

    // Play sound
    this.playSound();

    // Show Toast
    (this.toastr as any).info(notification.message, notification.title, {
      timeOut: 5000,
      closeButton: true,
      progressBar: true,
      positionClass: 'toast-top-right'
    });

    // Notify listeners
    this.notificationSubject.next(notification);

    // Increment unread count optimistically
    this.unreadCount.update(count => count + 1);
  }

  /**
   * API Methods
   */

  getNotifications(page: number = 1, perPage: number = 20): Observable<NotificationsData> {
    // console.log(`[NotificationService] Fetching notifications: page=${page}, perPage=${perPage}`);
    return this.http.get<ApiResponse<NotificationsData>>(`${this.apiUrl}notifications`, {
      params: { page: page.toString(), per_page: perPage.toString() }
    }).pipe(
      map(res => {
        // console.log('[NotificationService] Raw API Response:', res);
        return res.data;
      }),
      tap(data => {
        // console.log('[NotificationService] Received data:', data);
        if (data && data.meta) {
          // console.log('[NotificationService] Setting unread count to:', data.meta.unread_count);
          this.unreadCount.set(data.meta.unread_count);
        } else {
          // console.warn('[NotificationService] Response is missing meta/unread_count', data);
        }
      }),
      catchError((err: any) => {
        // console.error('[NotificationService] API Call Failed:', err);
        return throwError(() => err);
      })
    );
  }

  markAsRead(id: number | string): Observable<ActionResponse> {
    return this.http.post<ApiResponse<ActionResponse>>(`${this.apiUrl}notifications/mark-as-read`, { id }).pipe(
      map(res => res.data),
      tap(data => this.unreadCount.set(data.unread_count))
    );
  }

  deleteNotification(id: number | string): Observable<ActionResponse> {
    return this.http.delete<ApiResponse<ActionResponse>>(`${this.apiUrl}notifications/${id}`).pipe(
      map(res => res.data),
      tap(data => this.unreadCount.set(data.unread_count))
    );
  }

  setUnreadCount(count: number): void {
    this.unreadCount.set(count);
  }

  requestPermission(): void {
    if ('Notification' in window) {
      (window as any).Notification.requestPermission();
    }
  }
}
